/**
 * parseUserRoles — isolated utility for parsing Cognito custom attributes into role data.
 *
 * Cognito custom attributes are strings only. `custom:userTypes` stores a JSON-serialised
 * array (e.g. '["brand","influencer"]'). This utility centralises the parse/merge logic
 * so AuthContext and authCache always use the same code path and failures surface visibly
 * rather than being silently swallowed.
 *
 * @param {Object|null} attributes - Cognito user attributes object
 * @returns {{ roles: string[], activeRole: string|null, originalUserType: string|null }}
 */
export function parseUserRoles(attributes) {
  if (!attributes) return { roles: [], activeRole: null, originalUserType: null };

  const originalUserType = attributes['custom:user_type'] || null;
  const primaryRole      = attributes['custom:primaryRole'] || null;

  let userTypes = [];
  const userTypesRaw = attributes['custom:userTypes'];
  if (userTypesRaw) {
    try {
      const parsed = JSON.parse(userTypesRaw);
      if (Array.isArray(parsed)) {
        userTypes = parsed;
      } else {
        console.warn('[parseUserRoles] custom:userTypes parsed but is not an array:', userTypesRaw);
      }
    } catch (e) {
      console.warn('[parseUserRoles] Failed to parse custom:userTypes:', userTypesRaw);
    }
  }

  // Build roles list: prefer parsed userTypes; fall back to originalUserType
  let roles = userTypes.length > 0 ? userTypes : (originalUserType ? [originalUserType] : []);

  // Ensure originalUserType is always represented (handles transition period where
  // custom:userTypes may not yet contain the registration-time type)
  if (originalUserType && !roles.includes(originalUserType)) {
    roles = [...roles, originalUserType];
  }

  const activeRole = primaryRole || (roles.length > 0 ? roles[0] : null);

  return { roles, activeRole, originalUserType };
}
